import { ArrowSquareOut, Lightning, Star } from "@phosphor-icons/react";

function StatusPill({ status }) {
    const map = {
        "Email found": "dot-success",
        "Phone only": "dot-warning",
        "Website only": "dot-muted",
        "No contact found": "dot-error",
        "Duplicate skipped": "dot-muted",
    };
    const cls = map[status] || "dot-muted";
    return (
        <span className="mono text-[11px] whitespace-nowrap">
            <span className={"dot " + cls} />
            {status || "—"}
        </span>
    );
}

function DiffBadge({ diff }) {
    if (diff === "new") {
        return (
            <span
                className="mono text-[9px] px-1.5 py-0.5 bg-black text-white"
                data-testid="diff-new"
            >
                NEW
            </span>
        );
    }
    if (diff === "updated") {
        return (
            <span
                className="mono text-[9px] px-1.5 py-0.5 border border-[#b07000] text-[#b07000]"
                data-testid="diff-updated"
            >
                UPDATED
            </span>
        );
    }
    return null;
}

function PriorityBadge({ label }) {
    if (!label) return <span className="text-[#a1a1aa] mono text-[10px]">—</span>;
    const color =
        label === "High" ? "#002FA7" : label === "Medium" ? "#b07000" : "#a1a1aa";
    const Icon = label === "High" ? Lightning : label === "Medium" ? Star : null;
    return (
        <span
            className="inline-flex items-center gap-1 mono text-[10px] px-2 py-0.5 whitespace-nowrap"
            style={{ color, border: `1px solid ${color}` }}
            data-testid={`priority-${label.toLowerCase()}`}
        >
            {Icon && <Icon size={10} weight="fill" />}
            {label}
        </span>
    );
}

function SourceTag({ source, sources }) {
    const list = (sources && sources.length) ? sources : source ? [source] : [];
    if (list.length === 0) {
        return <span className="text-[#a1a1aa] mono text-[10px]">—</span>;
    }
    const abbr = (s) => {
        if (s === "yellow.co.nz") return "Y";
        if (s === "finda.co.nz") return "F";
        if (s === "localist.co.nz") return "L";
        if (s === "google") return "W";
        return (s || "?").slice(0, 1).toUpperCase();
    };
    return (
        <span className="inline-flex gap-1">
            {list.map((s) => (
                <span
                    key={s}
                    className="inline-flex items-center justify-center mono text-[10px]"
                    style={{
                        width: 22,
                        height: 22,
                        border: "1px solid #000",
                        color: "#000",
                    }}
                    title={s}
                >
                    {abbr(s)}
                </span>
            ))}
        </span>
    );
}

export default function ResultsTable({ rows, loading }) {
    if (loading && rows.length === 0) {
        return (
            <div className="p-10 text-center text-[#52525b] text-sm" data-testid="results-loading">
                Searching, please wait…
            </div>
        );
    }

    if (!loading && rows.length === 0) {
        return (
            <div
                className="p-10 text-center text-[#52525b] text-sm"
                data-testid="results-empty"
            >
                No results yet. Start a search to populate this table.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto" data-testid="results-table-wrap">
            <table className="brutal-table" data-testid="results-table">
                <thead>
                    <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>Provider Name</th>
                        <th>Priority</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Website</th>
                        <th>Source</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.website + r.name + i} data-testid={`result-row-${i}`}>
                            <td className="mono text-[11px] text-[#a1a1aa]">
                                {String(i + 1).padStart(2, "0")}
                            </td>
                            <td>
                                <div className="font-medium flex items-center gap-2">
                                    {r.name || "—"}
                                    <DiffBadge diff={r.diff_status} />
                                </div>
                                {r.confidence && r.email && (
                                    <div className="label-kicker mt-0.5" style={{ fontSize: 9 }}>
                                        {r.confidence} confidence
                                    </div>
                                )}
                            </td>
                            <td>
                                <PriorityBadge label={r.priority_label} />
                            </td>
                            <td className="mono text-xs break-all">
                                {r.email || <span className="text-[#a1a1aa]">—</span>}
                            </td>
                            <td className="mono text-xs whitespace-nowrap">
                                {r.phone || <span className="text-[#a1a1aa]">—</span>}
                            </td>
                            <td className="mono text-xs">
                                {r.website ? (
                                    <a
                                        href={r.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 underline underline-offset-2"
                                    >
                                        {new URL(r.website).host.replace(/^www\./, "")}
                                        <ArrowSquareOut size={11} />
                                    </a>
                                ) : (
                                    <span className="text-[#a1a1aa]">—</span>
                                )}
                            </td>
                            <td>
                                <SourceTag source={r.source} sources={r.sources} />
                            </td>
                            <td>
                                <StatusPill status={r.status} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
